import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import client, { getToken, setToken, clearToken, getActing, setActing } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // pending2fa holds { email } while the user enters their emailed code
  const [pending2fa, setPending2fa] = useState(null);
  // acting: staff impersonating a student -> { user_id, name }
  const [acting, setActingState] = useState(() => getActing());

  useEffect(() => {
    const boot = async () => {
      // CRITICAL: If returning from Google OAuth callback, skip the /me check.
      // AuthCallback will exchange the session_id and establish the session first.
      if (window.location.hash && window.location.hash.includes("session_id=")) {
        setLoading(false);
        return;
      }
      const token = getToken();
      if (token) {
        try {
          const res = await client.get("/auth/me", { timeout: 12000 });
          setUser(res.data);
        } catch (e) {
          // Network error / timeout / invalid token — never leave the app stuck.
          if (e?.response?.status === 401) clearToken();
        }
      }
      setLoading(false);
    };
    boot();
  }, []);

  // Usage heartbeat: one ping a minute while a tab is open and visible. The
  // backend only credits time between consecutive pings, so idle or closed
  // tabs don't inflate anyone's hours.
  useEffect(() => {
    if (!user) return;
    let stopped = false;
    const ping = () => {
      if (stopped || document.visibilityState !== "visible") return;
      client.post("/activity/ping", { acting_as: acting?.user_id || null }).catch(() => {});
    };
    ping();
    const id = setInterval(ping, 60000);
    document.addEventListener("visibilitychange", ping);
    return () => {
      stopped = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", ping);
    };
  }, [user, acting]);

  // Establish session after Google OAuth (called by AuthCallback).
  const applyGoogleSession = useCallback((token, profile) => {
    setToken(token);
    setUser(profile);
  }, []);

  const errMsg = (e, fallback) =>
    e?.response?.data?.detail || fallback || "Something went wrong.";

  // Step 1: validate credentials -> backend emails a 2FA code (or reports an unapproved account).
  const requestLogin = useCallback(async ({ email, password, name }) => {
    try {
      const res = await client.post("/auth/request-otp", { email, password, name, language: localStorage.getItem("mb_lang") || "en" });
      // Unapproved accounts never get a code or a session.
      if (!res.data?.pending) setPending2fa({ email });
      return res.data; // { sent, email, dev_code? } | { pending, status, new? }
    } catch (e) {
      throw new Error(errMsg(e, "Couldn't sign in."));
    }
  }, []);

  // Step 2: verify the emailed 2FA code.
  const verify2fa = useCallback(
    async (code) => {
      if (!pending2fa) throw new Error("Session expired. Please sign in again.");
      try {
        const res = await client.post("/auth/verify-otp", { email: pending2fa.email, code: String(code).trim() });
        setToken(res.data.token);
        setUser(res.data.user);
        setPending2fa(null);
        return res.data.user;
      } catch (e) {
        throw new Error(errMsg(e, "Verification failed."));
      }
    },
    [pending2fa]
  );

  const resend2fa = useCallback(async (password) => {
    if (!pending2fa) return null;
    try {
      const res = await client.post("/auth/request-otp", { email: pending2fa.email, password, language: localStorage.getItem("mb_lang") || "en" });
      return res.data;
    } catch (e) {
      return null;
    }
  }, [pending2fa]);

  const cancel2fa = useCallback(() => setPending2fa(null), []);

  const logout = useCallback(async () => {
    try { await client.post("/auth/logout"); } catch (e) { console.error("Logout call failed; clearing locally", e); }
    clearToken();
    setActing(null);
    setActingState(null);
    setUser(null);
    setPending2fa(null);
  }, []);

  const actAs = useCallback((student) => {
    const s = { user_id: student.user_id, name: student.name, self: !!student.self };
    setActing(s);
    setActingState(s);
  }, []);

  const stopActing = useCallback(() => {
    setActing(null);
    setActingState(null);
  }, []);

  const isStaff = user && (user.role === "admin" || user.role === "super_admin");
  const isSuper = user && user.role === "super_admin";

  const value = {
    user, loading, pending2fa, acting, isStaff, isSuper,
    requestLogin, verify2fa, resend2fa, cancel2fa, logout, applyGoogleSession,
    actAs, stopActing,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
